import type { CoreConsentsResponse } from "@tandemdiabetes/sugarmate-api-react-client"
import { Form } from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import type { AccountFormProps } from "../AccountFormProps"
import styles from "../AccountForms.module.scss"
import { ConsentCheckbox } from "./ConsentCheckbox"
import { ConsentTextWithLinks } from "./ConsentTextWithLinks"
import customStyles from "./RequiredConsentNoticesForm.module.scss"

import { usePolyglot } from "src/contexts"
import type { RegistrationForm } from "src/pages/SignUp"

const polyglotPrefix = "components.account_forms.required_consent_notices."

interface ConsentNotices {
  consents: RegistrationForm["consents"];
}

interface Props extends AccountFormProps<ConsentNotices> {
  consents: CoreConsentsResponse["required"];
}

export function RequiredConsentNoticesForm(props: Props) {
  const {
    form,
    children,
    onSubmit,
    updateForm,
    consents,
  } = props

  const polyglot = usePolyglot()

  const onToggleConsent = useCallback(
    (policyId: string) => {
      updateForm({
        consents: {
          ...form.consents,
          [policyId]: !form.consents[policyId],
        },
      })
    },
    [
      updateForm,
      form.consents,
    ],
  )

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div>
        <div className={styles.subHeading}>
          {polyglot.t(`${polyglotPrefix}sub_title`)}
        </div>
      </div>

      {consents?.headers.map((header, index) => (
        <div
          key={index}
          className={customStyles.consentsHeader}
        >
          <ConsentTextWithLinks
            text={header.text}
            links={header.links || {}}
          />
        </div>
      ))}

      <div className={customStyles.checkboxes}>
        <div className={customStyles.checkFollowingBoxes}>
          {polyglot.t(`${polyglotPrefix}checkboxes_title`)}
        </div>

        {consents?.consents.map((consent, index) => (
          <ConsentCheckbox
            key={`consent-${index}`}
            consent={consent}
            checked={form.consents[consent.policyId]}
            onToggleConsent={onToggleConsent}
            required
          />
        ))}
      </div>

      {consents?.footers.map((footer, index) => (
        <div
          key={index}
          className={customStyles.consentsFooter}
        >
          <ConsentTextWithLinks
            text={footer.text}
            links={footer.links || {}}
          />
        </div>
      ))}

      {children}
    </Form>
  )
}
